import React, { useMemo, useState } from 'react';
import { Input, Select, Tag } from 'antd';
import CommonTable from 'components/CommonTable';
import { applications, GPApplication, formatDate } from '../mockData';
import s from './s.module.less';

const statusColorMap: Record<string, string> = {
    submitted: 'gold',
    under_review: 'blue',
    approved: 'green',
    rejected: 'red',
    more_info_needed: 'orange',
};

const statusLabelMap: Record<string, string> = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    more_info_needed: 'More Info Needed',
};

const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Under Review', value: 'under_review' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'More Info Needed', value: 'more_info_needed' },
];

const GPApplicationsPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const pendingCount = useMemo(() => {
        return applications.filter((a) => a.status === 'submitted' || a.status === 'under_review').length;
    }, []);

    const filteredData = useMemo(() => {
        return applications.filter((app) => {
            const matchSearch = !search || app.practiceName.toLowerCase().includes(search.toLowerCase()) || app.email.toLowerCase().includes(search.toLowerCase());
            const matchStatus = !statusFilter || app.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [search, statusFilter]);

    const columns = [
        {
            title: 'Practice Name',
            dataIndex: 'practiceName',
            key: 'practiceName',
            render: (_: string, record: GPApplication) => (
                <div>
                    <div className={s.practiceName}>{record.practiceName}</div>
                    <div className={s.practiceEmail}>{record.email}</div>
                </div>
            ),
        },
        {
            title: 'Contact',
            dataIndex: 'contactPerson',
            key: 'contactPerson',
        },
        {
            title: 'Specialty',
            dataIndex: 'specialty',
            key: 'specialty',
        },
        {
            title: 'Providers',
            dataIndex: 'providerCount',
            key: 'providerCount',
            width: 90,
        },
        {
            title: 'Submitted',
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            render: (val: string) => formatDate(val),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={statusColorMap[status]}>{statusLabelMap[status]}</Tag>
            ),
        },
        {
            title: 'Reviewer',
            dataIndex: 'assignedReviewer',
            key: 'assignedReviewer',
        },
        {
            title: 'Action',
            key: 'action',
            render: () => (
                <span className={s.viewLink}>View</span>
            ),
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h2>Application Review</h2>
                <span className={s.pendingBadge}>{pendingCount} pending</span>
            </div>
            <div className={s.filters}>
                <Input
                    className={s.searchInput}
                    placeholder="Search by practice name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                />
                <Select
                    className={s.statusSelect}
                    placeholder="Filter by status"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={statusOptions}
                />
            </div>
            <CommonTable<GPApplication>
                data={filteredData}
                columns={columns}
                rowKey="id"
                pagination={false}
            />
        </div>
    );
};

export default GPApplicationsPage;
